import { useWorkspaceInfo } from '@affine/core/components/hooks/use-workspace-info';
import { ServerService } from '@affine/core/modules/cloud';
import type { SettingTab } from '@affine/core/modules/dialogs/constant';
import { GuardService } from '@affine/core/modules/permissions';
import {
  FEATURE_KEY_CUSTOM_APPS_CREATE,
  FEATURE_KEY_WORKFLOW,
  getWorkspaceMailMissingFeature,
  useFeatureGate,
} from '@affine/core/modules/streamlain/hooks/use-feature-gate';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { Permission } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import {
  AdminIcon,
  AiIcon,
  CollaborationIcon,
  EmailIcon,
  FolderIcon,
  GroupIcon,
  ImageIcon,
  IntegrationsIcon,
  KeyboardIcon,
  LinkIcon,
  LockPanelIcon,
  MultiPeopleIcon,
  PageIcon,
  PaymentIcon,
  PlayIcon,
  PluginIcon,
  PropertyIcon,
  ReauthenticationIcon,
  SaveIcon,
  SettingsIcon,
  SignalIcon,
  StarIcon,
} from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import { useMemo } from 'react';

import type { SettingSidebarItem, SettingState } from '../types';

// sl-4rt9: Narrow the sidebar key type to the SSoT list so any new tab
// added to the registry without a WORKSPACE_TAB_LOCAL_SUPPORT entry fails
// tsc at compile time (primary contract enforcement).
type WorkspaceSidebarItem = Omit<SettingSidebarItem, 'key'> & {
  key: RegisteredWorkspaceTab;
};
import { WorkspaceSettingAI } from './ai';
import { AuditTrailPanel } from './audit-trail/audit-trail-panel';
import { WorkspaceSettingAutomations } from './automations';
import { WorkspaceSettingBilling } from './billing';
import { WorkspaceSettingCredentials } from './credentials';
import { WorkspaceSettingCustomApps } from './custom-apps';
import { WorkspaceSettingFolders } from './folders';
import { GroupsPanel } from './groups';
import { GuestsPanel } from './guests';
import { IntegrationSetting, useHasAnyIntegration } from './integration';
import {
  LOCAL_UNSUPPORTED_TABS,
  type RegisteredWorkspaceTab,
} from './local-unsupported-tabs';
import { WorkspaceSettingMail } from './mail';
import { McpAccessPanel } from './mcp-access';
import { MembersPanel } from './members';
import { WorkspaceSettingPdfSignatures } from './pdf-signatures';
import { WorkspaceSettingPermissionGraph } from './permission-graph';
import { WorkspaceSettingPermissions } from './permissions';
import { WorkspaceSettingPlans } from './plans';
import { WorkspaceSettingDetail } from './preference';
import { WorkspaceSettingProperties } from './properties';
import { RolesConfiguration } from './roles';
import { SamlPanel } from './saml/saml-panel';
import { WorkspaceSettingSecurity } from './security';
import { WorkspaceSettingStorage } from './storage';
import { WorkspaceTeamspacesPanel } from './teamspaces/teamspaces-panel';
import { WorkspaceSettingWebhooks } from './webhooks';
import { WorkspaceSettingWorkflows } from './workflows';

// Admin-only tabs — content guard prevents direct tab rendering for non-admins.
// Credentials, Workflows, and Automations keep their own gates.
const ADMIN_ONLY_TABS = new Set<string>([
  'workspace:preference',
  'workspace:properties',
  'workspace:members',
  'workspace:groups',
  'workspace:guests',
  'workspace:security',
  'workspace:saml',
  'workspace:permissions',
  'workspace:folders',
  'workspace:team-areas',
  'workspace:roles',
  'workspace:permission-graph',
  'workspace:mcp-access',
  'workspace:ai',
  'workspace:integrations',
  'workspace:storage',
  'workspace:mail',
  'workspace:pdf-signatures',
  'workspace:plans',
  'workspace:billing',
  'workspace:audit-trail', // sl-wmbt.5.1
]);

const READER_BLOCKED_TABS = new Set<string>([
  'workspace:credentials',
  'workspace:webhooks',
  'workspace:workflows',
  'workspace:automations',
  'workspace:custom-apps',
]);

export const WorkspaceSetting = ({
  activeTab,
  onCloseSetting,
  onChangeSettingState,
}: {
  activeTab: SettingTab;
  onCloseSetting: () => void;
  onChangeSettingState: (settingState: SettingState) => void;
}) => {
  const workspaceService = useService(WorkspaceService);
  const guardService = useService(GuardService);
  const information = useWorkspaceInfo(workspaceService.workspace);
  const isAdmin = information?.isOwner || information?.isAdmin;
  const isReader = information?.role === Permission.Reader;
  const isLocalWorkspace = workspaceService.workspace.flavour === 'local';
  const canEditCustomApps = useLiveData(
    guardService.can$('Workspace_CustomApps_Edit')
  );
  const { isFeatureAllowed } = useFeatureGate();

  // Guard: render nothing for admin-only tabs when user is not admin
  if (ADMIN_ONLY_TABS.has(activeTab) && !isAdmin) {
    return null;
  }

  if (isReader && READER_BLOCKED_TABS.has(activeTab)) {
    return null;
  }

  if (
    activeTab === 'workspace:custom-apps' &&
    !(isAdmin || canEditCustomApps === true)
  ) {
    return null;
  }

  // Local workspaces cannot render cloud-only tabs (see LOCAL_UNSUPPORTED_TABS).
  if (isLocalWorkspace && LOCAL_UNSUPPORTED_TABS.has(activeTab)) {
    return null;
  }

  if (
    activeTab === 'workspace:mail' &&
    getWorkspaceMailMissingFeature(isFeatureAllowed) !== null
  ) {
    return null;
  }

  switch (activeTab) {
    case 'workspace:preference':
      return <WorkspaceSettingDetail onCloseSetting={onCloseSetting} />;
    case 'workspace:properties':
      return <WorkspaceSettingProperties />;
    case 'workspace:roles':
      return <RolesConfiguration />;
    case 'workspace:permission-graph':
      return <WorkspaceSettingPermissionGraph />;
    case 'workspace:members':
      return (
        <MembersPanel
          onCloseSetting={onCloseSetting}
          onChangeSettingState={onChangeSettingState}
        />
      );
    case 'workspace:guests':
      return <GuestsPanel />;
    case 'workspace:groups':
      return <GroupsPanel />;
    case 'workspace:permissions':
      return <WorkspaceSettingPermissions onCloseSetting={onCloseSetting} />;
    case 'workspace:folders':
      return <WorkspaceSettingFolders onCloseSetting={onCloseSetting} />;
    case 'workspace:team-areas':
      return <WorkspaceTeamspacesPanel />;
    case 'workspace:plans':
      return <WorkspaceSettingPlans />;
    case 'workspace:billing':
      return (
        <WorkspaceSettingBilling onChangeSettingState={onChangeSettingState} />
      );
    case 'workspace:storage':
      return (
        <WorkspaceSettingStorage
          onCloseSetting={onCloseSetting}
          onChangeSettingState={onChangeSettingState}
        />
      );
    case 'workspace:mcp-access':
      return <McpAccessPanel />;
    case 'workspace:integrations':
      return <IntegrationSetting onChangeSettingState={onChangeSettingState} />;
    case 'workspace:credentials':
      return <WorkspaceSettingCredentials />;
    case 'workspace:workflows':
      return <WorkspaceSettingWorkflows />;
    case 'workspace:automations':
      return <WorkspaceSettingAutomations />;
    case 'workspace:webhooks':
      return <WorkspaceSettingWebhooks />;
    case 'workspace:ai':
      return <WorkspaceSettingAI onChangeSettingState={onChangeSettingState} />;
    case 'workspace:security':
      return <WorkspaceSettingSecurity />;
    case 'workspace:saml':
      return <SamlPanel />;
    case 'workspace:audit-trail':
      return <AuditTrailPanel />;
    case 'workspace:custom-apps':
      return (
        <WorkspaceSettingCustomApps
          onChangeSettingState={onChangeSettingState}
        />
      );
    case 'workspace:mail':
      return <WorkspaceSettingMail />;
    case 'workspace:pdf-signatures':
      return <WorkspaceSettingPdfSignatures />;
    default:
      return null;
  }
};

export const useWorkspaceSettingList = (): SettingSidebarItem[] => {
  const workspaceService = useService(WorkspaceService);
  const information = useWorkspaceInfo(workspaceService.workspace);
  const serverService = useService(ServerService);
  const hasPaymentFeature = useLiveData(
    serverService.server.features$.map(f => f?.payment)
  );
  // Local workspaces have no cloud subscription/billing context — Plans
  // and Billing tabs cannot render (WorkspaceSubscriptionService.server
  // is null, Suspended credit queries fail auth). Hide both outright.
  const isLocalWorkspace = workspaceService.workspace.flavour === 'local';
  const { isFeatureAllowed } = useFeatureGate();
  const hasRolesAndAccess = isFeatureAllowed('roles-and-access');
  const hasRolePermissionsConfig = isFeatureAllowed('role-permissions-config');
  const hasManagedAI = isFeatureAllowed('managed-ai-credits');
  const hasCustomAppsCreate = isFeatureAllowed(FEATURE_KEY_CUSTOM_APPS_CREATE);
  const hasSaml = isFeatureAllowed('sso-saml');
  const hasWorkflows = isFeatureAllowed(FEATURE_KEY_WORKFLOW);
  const hasWorkspaceMail =
    getWorkspaceMailMissingFeature(isFeatureAllowed) === null;
  const hasAuditTrail = isFeatureAllowed('audit-trail');
  // Tab-visibility gates that are orthogonal to the underlying feature
  // (e.g. every plan gets Members, but enterprise may hide the tab).
  // Plan matrix decides via plan_features; local piggybacks on Free.
  const hasMembersTab = isFeatureAllowed('members-tab');
  const hasCredentialsTab = isFeatureAllowed('credentials-tab');
  const hasAutomationsTab = isFeatureAllowed('automations-tab');
  // Hide the integrations tab entirely when no card or vertical applies
  // to this workspace flavour (e.g. local web workspace).
  const hasAnyIntegration = useHasAnyIntegration();
  // Permission-based visibility for Custom Apps tab.
  const guardService = useService(GuardService);
  const canEditCustomApps = useLiveData(
    guardService.can$('Workspace_CustomApps_Edit')
  );

  const t = useI18n();
  const items = useMemo<WorkspaceSidebarItem[]>(() => {
    const isAdmin = information?.isOwner || information?.isAdmin;
    const isReader = information?.role === Permission.Reader;
    const isGuest = information?.isGuest;

    // sl-1dyb: Guests see nothing in workspace settings
    if (isGuest) return [];

    // sl-4rt9: Explicit element typing forces tsc to check each inline
    // object's `key` against RegisteredWorkspaceTab at construction time.
    // Without this, the `.filter((item): item is WorkspaceSidebarItem …)`
    // predicate below would launder a widened `{ key: string, … }` into
    // WorkspaceSidebarItem and silently bypass the SSoT contract.
    //
    // DO NOT silence a TS2322 here with any form of indirection:
    //   - `as RegisteredWorkspaceTab` / `<T>` cast on the key literal
    //   - typed const or expression as the key (`key: SOME_CONST`)
    //   - array-level spread (`...extraTabs`)
    //   - object-level spread (`{ key: '…', ...override }`)
    //   - computed property name (`{ [shadow]: '…' }`)
    //   - quoted property name (`{ 'key': '…' }`)
    //   - method / getter / setter / shorthand property named `key`
    // Each of those hides the tab from LOCAL_UNSUPPORTED_TABS, the
    // URL-direct-nav guard, and the render switch below. Instead, add
    // the new tab to WORKSPACE_TAB_LOCAL_SUPPORT in local-unsupported-tabs.ts
    // with the correct classification.
    // __tests__/local-unsupported-tabs.spec.ts AST-inspects `rawItems` and
    // rejects every such form with a pointed error naming the line.
    type RegistryEntry = WorkspaceSidebarItem | false | null | undefined;

    // Semantic groups: General, People & Access, AI & Governance,
    // Automation, Apps & Integrations, Plan & Billing.
    const rawItems: RegistryEntry[] = [
      // --- General ---
      isAdmin && {
        key: 'workspace:preference',
        title: t['com.streamlain.settings.workspace.profile'](),
        icon: <SettingsIcon />,
        testId: 'workspace-setting:preference',
        section: t['com.streamlain.settings.workspace.section.general'](),
      },
      // sl-1dyb: properties admin-only
      isAdmin && {
        key: 'workspace:properties',
        title: t['com.streamlain.settings.workspace.properties'](),
        icon: <PropertyIcon />,
        testId: 'workspace-setting:properties',
        section: t['com.streamlain.settings.workspace.section.general'](),
      },
      // sl-1dyb: storage admin-only
      isAdmin && {
        key: 'workspace:storage',
        title: t['Storage'](),
        icon: <SaveIcon />,
        testId: 'workspace-setting:storage',
        section: t['com.streamlain.settings.workspace.section.general'](),
      },
      // --- People & Access ---
      hasMembersTab &&
        isAdmin && {
          key: 'workspace:members',
          title: t['Members'](),
          icon: <CollaborationIcon />,
          testId: 'workspace-setting:members',
          section:
            t['com.streamlain.settings.workspace.section.people-access'](),
        },
      hasRolesAndAccess &&
        isAdmin && {
          key: 'workspace:guests',
          title: t['com.streamlain.settings.workspace.guests.title'](),
          icon: <GroupIcon />,
          testId: 'workspace-setting:guests',
          section:
            t['com.streamlain.settings.workspace.section.people-access'](),
        },
      hasRolesAndAccess &&
        isAdmin && {
          key: 'workspace:groups',
          title:
            t['com.streamlain.settings.workspace.groups.title']?.() ?? 'Groups',
          icon: <MultiPeopleIcon />,
          testId: 'workspace-setting:groups',
          section:
            t['com.streamlain.settings.workspace.section.people-access'](),
        },
      // sl-sdk6.7.3: Roles Configuration Tab — Beta-only (own feature gate)
      hasRolePermissionsConfig &&
        isAdmin && {
          key: 'workspace:roles',
          title: t['com.streamlain.settings.workspace.roles.title'](),
          icon: <AdminIcon />,
          testId: 'workspace-setting:roles',
          section:
            t['com.streamlain.settings.workspace.section.people-access'](),
        },
      hasRolesAndAccess &&
        isAdmin && {
          key: 'workspace:permission-graph',
          title:
            t['com.streamlain.settings.workspace.permission-graph.title']?.() ??
            'Permission Graph',
          icon: <AdminIcon />,
          testId: 'workspace-setting:permission-graph',
          section:
            t['com.streamlain.settings.workspace.section.people-access'](),
        },
      hasRolesAndAccess &&
        isAdmin && {
          key: 'workspace:permissions',
          title:
            t['com.streamlain.settings.workspace.permissions']?.() ??
            'Documents',
          icon: <PageIcon />,
          testId: 'workspace-setting:permissions',
          section:
            t['com.streamlain.settings.workspace.section.people-access'](),
        },
      hasRolesAndAccess &&
        isAdmin && {
          key: 'workspace:folders',
          title:
            t['com.streamlain.settings.workspace.folders']?.() ?? 'Folders',
          icon: <FolderIcon />,
          testId: 'workspace-setting:folders',
          section:
            t['com.streamlain.settings.workspace.section.people-access'](),
        },
      hasRolesAndAccess &&
        isAdmin && {
          key: 'workspace:team-areas',
          title:
            t['com.streamlain.settings.workspace.teamspaces.title']?.() ??
            'Team Areas',
          icon: <FolderIcon />,
          testId: 'workspace-setting:team-areas',
          section:
            t['com.streamlain.settings.workspace.section.people-access'](),
        },
      // Security follows the document/folder access controls.
      hasRolesAndAccess &&
        isAdmin && {
          key: 'workspace:security',
          title: t['com.streamlain.settings.workspace.security.title'](),
          icon: <LockPanelIcon />,
          testId: 'workspace-setting:security',
          section:
            t['com.streamlain.settings.workspace.section.people-access'](),
        },
      // sl-sc2d: SAML SSO settings (Enterprise+)
      hasSaml &&
        isAdmin && {
          key: 'workspace:saml',
          title: t['com.streamlain.settings.workspace.saml.title'](),
          icon: <KeyboardIcon />,
          testId: 'workspace-setting:saml',
          section:
            t['com.streamlain.settings.workspace.section.people-access'](),
        },
      // sl-xk37: MCP Access Management (Admin/Owner only, gated by MCP feature)
      isAdmin &&
        isFeatureAllowed('mcp') && {
          key: 'workspace:mcp-access',
          title:
            t['com.streamlain.settings.workspace.mcp-access.title']?.() ??
            'MCP Access',
          icon: <PluginIcon />,
          testId: 'workspace-setting:mcp-access',
          section:
            t['com.streamlain.settings.workspace.section.people-access'](),
        },
      // Per-workflow access control belongs with the other people/access
      // surfaces, not with runtime and execution-limit configuration.
      hasWorkflows &&
        !isReader && {
          key: 'workspace:workflows',
          title: t['com.streamlain.settings.workspace.workflows.title'](),
          icon: <PlayIcon />,
          testId: 'workspace-setting:workflows',
          section:
            t['com.streamlain.settings.workspace.section.people-access'](),
        },
      // sl-wmbt.5.1: Audit Trail — feature-gated (Team+ only; panel shows
      // upgrade prompt for plans that include the tab but not the feature)
      hasAuditTrail &&
        isAdmin && {
          key: 'workspace:audit-trail',
          title: t['com.streamlain.settings.workspace.audit-trail.title'](),
          icon: <PageIcon />,
          testId: 'workspace-setting:audit-trail',
          section:
            t['com.streamlain.settings.workspace.section.people-access'](),
        },
      // --- AI & Governance ---
      hasManagedAI &&
        isAdmin && {
          key: 'workspace:ai',
          title:
            t['com.streamlain.settings.workspace.ai.title']?.() ??
            'AI Settings',
          icon: <AiIcon />,
          testId: 'workspace-setting:ai',
          section:
            t['com.streamlain.settings.workspace.section.ai-governance'](),
        },
      // --- Automation ---
      hasWorkflows &&
        !isReader && {
          key: 'workspace:webhooks',
          title:
            t['com.streamlain.settings.workspace.webhooks.title']?.() ??
            'Webhooks',
          icon: <SignalIcon />,
          testId: 'workspace-setting:webhooks',
          section: t['com.streamlain.settings.workspace.section.automation'](),
        },
      hasAutomationsTab &&
        !isReader && {
          key: 'workspace:automations',
          title:
            t['com.streamlain.settings.workspace.automations.title']?.() ??
            'Workflow Limits',
          icon: <PlayIcon />,
          testId: 'workspace-setting:automations',
          section: t['com.streamlain.settings.workspace.section.automation'](),
        },
      // --- Apps & Integrations ---
      // sl-h128.2.1: AI Memory + Expert Agents consolidated into 'workspace:ai' tab
      // Custom Apps settings require edit rights; view-only site access is not enough
      // for workspace settings.
      hasCustomAppsCreate &&
        !isReader &&
        (isAdmin || canEditCustomApps === true) && {
          key: 'workspace:custom-apps',
          title: t['com.streamlain.settings.workspace.customApps.title'](),
          icon: <LinkIcon />,
          testId: 'workspace-setting:custom-apps',
          section:
            t['com.streamlain.settings.workspace.section.apps-integrations'](),
        },
      // sl-1dyb: integrations admin-only — hidden when no card/vertical applies
      isAdmin &&
        hasAnyIntegration && {
          key: 'workspace:integrations',
          title: t['com.streamlain.integration.integrations'](),
          icon: <IntegrationsIcon />,
          testId: 'workspace-setting:integrations',
          section:
            t['com.streamlain.settings.workspace.section.apps-integrations'](),
        },
      isAdmin &&
        hasWorkspaceMail && {
          key: 'workspace:mail',
          title: t['com.streamlain.rootAppSidebar.email'](),
          icon: <EmailIcon />,
          testId: 'workspace-setting:mail',
          section:
            t['com.streamlain.settings.workspace.section.apps-integrations'](),
        },
      // Editors see owned/shared credentials; Readers do not get this surface.
      hasCredentialsTab &&
        !isReader && {
          key: 'workspace:credentials',
          title: t['com.streamlain.settings.workspace.credentials.title'](),
          icon: <ReauthenticationIcon />,
          testId: 'workspace-setting:credentials',
          section:
            t['com.streamlain.settings.workspace.section.apps-integrations'](),
        },
      isAdmin && {
        key: 'workspace:pdf-signatures',
        title: t['com.streamlain.pdf.signature.settings.title'](),
        icon: <ImageIcon />,
        testId: 'workspace-setting:pdf-signatures',
        section:
          t['com.streamlain.settings.workspace.section.apps-integrations'](),
      },
      // --- Plan & Billing ---
      // `!isLocalWorkspace` gate is applied centrally via LOCAL_UNSUPPORTED_TABS
      // filter at the end of this list, so we only check plan/role here.
      hasPaymentFeature &&
        (information?.isOwner || information?.isAdmin) && {
          key: 'workspace:plans',
          title: t['com.streamlain.payment.title'](),
          icon: <StarIcon />,
          testId: 'workspace-setting:plans',
          section:
            t['com.streamlain.settings.workspace.section.plan-billing'](),
        },
      hasPaymentFeature &&
        isAdmin && {
          key: 'workspace:billing',
          title: t['com.streamlain.settings.workspace.billing'](),
          icon: <PaymentIcon />,
          testId: 'workspace-setting:billing',
          section:
            t['com.streamlain.settings.workspace.section.plan-billing'](),
        },
    ];

    return rawItems
      .filter((item): item is WorkspaceSidebarItem => !!item)
      .filter(
        item => !isLocalWorkspace || !LOCAL_UNSUPPORTED_TABS.has(item.key)
      );
  }, [
    information,
    t,
    hasPaymentFeature,
    isLocalWorkspace,
    hasRolesAndAccess,
    hasRolePermissionsConfig,
    hasManagedAI,
    hasSaml,
    hasWorkflows,
    hasWorkspaceMail,
    hasAuditTrail,
    hasMembersTab,
    hasCredentialsTab,
    hasAutomationsTab,
    hasCustomAppsCreate,
    hasAnyIntegration,
    canEditCustomApps,
    isFeatureAllowed,
  ]);

  return items;
};
